<template>
  <v-card
    :key="member.id"
    :border="border"
    tile
  >
    <v-card-title v-if="member.id && !hideMemberHeader">
      <v-row
        class="d-flex align-center"
        dense
      >
        <v-col class="d-flex align-center">
          <v-avatar
            v-if="member.first_name"
            class="fs-18 c-white me-4"
            color="primary"
            size="40"
          >
            {{ [member.first_name[0], member.last_name[0]].join('').toUpperCase() }}
          </v-avatar>

          <div class="w-100pc">
            <div
              v-text="
                member.first_name
                  ? [member.first_name, member.last_name].join(' ')
                  : $bct('Unknown name')
              "
              class="fs-16 fw-600 mb-0 w-100pc word-break-word"
            />

            <div
              v-text="member.email"
              class="fs-14 c-light-black w-100pc word-break-word"
            />
          </div>
        </v-col>

        <!-- Temporarily hidden as part of CARE-3753 to mitigate employment bug -->
        <!-- <v-col class="d-flex justify-end">
          <template v-if="!readOnly && member.end_date == null">
            <ActionMenu
              @click:action:delete="dismissStaffMember"
              :items="memberActionItems"
              button-icon="more_vert"
              left
            />
          </template>
        </v-col> -->
      </v-row>
    </v-card-title>

    <v-divider v-if="member.id && !hideMemberHeader" />

    <v-card-text>
      <v-row>
        <LabeledTextfield
          v-model="member.first_name"
          @input="changed = true"
          :cols="contactFieldCols"
          :hard-lock="readOnly"
          message="First name"
        />

        <LabeledTextfield
          v-model="member.last_name"
          @input="changed = true"
          :cols="contactFieldCols"
          :hard-lock="readOnly"
          message="Last name"
        />

        <LabeledTextfield
          v-model="member.email"
          @input="changed = true"
          :cols="contactFieldCols"
          :hard-lock="readOnly"
          message="Email"
        />

        <LabeledTextfield
          v-if="fourColumnContactLayout"
          v-model="member.phone"
          @input="changed = true"
          :cols="contactFieldCols"
          :hard-lock="readOnly"
          mask="(###) ###-####"
          message="Phone"
        />

        <LabeledSimpleSelect
          v-model="member.business_role"
          @input="changed = true"
          :cols="employmentFieldCols"
          :hard-lock="readOnly"
          :items="['Admin', 'Member']"
          message="Role"
        />

        <LabeledTextfield
          v-model="member.title"
          @input="changed = true"
          :cols="employmentFieldCols"
          :hard-lock="readOnly"
          message="Job title"
        />

        <LabeledAutocomplete
          v-if="providers.length > 1 && !hideLocationsField"
          v-model="member.provider_ids"
          @input="changed = true"
          :cols="employmentFieldCols"
          :hard-lock="readOnly"
          :items="providers"
          :placeholder="$bct('All locations')"
          item-title="name"
          item-value="id"
          message="Locations"
          chips
          deletable-chips
          multiple
        />

        <LabeledDatePicker
          v-model="member.start_date"
          @input="changed = true"
          :cols="employmentFieldCols"
          :hard-lock="readOnly"
          message="Employment start date"
        />

        <LabeledDatePicker
          v-model="member.end_date"
          @input="changed = true"
          :cols="employmentFieldCols"
          :hard-lock="isSpecialist || readOnly"
          message="Employment end date"
        />

        <LabeledSwitch
          v-model="member.business_messages_access"
          @input="changed = true"
          :cols="fourColumnEmploymentLayout ? 12 : 4"
          :hard-lock="readOnly"
          subtitle="Can access instant messages and respond to messages from organization staff and families."
          title="Messaging"
        />
      </v-row>

      <v-row
        v-if="!hideEmploymentAlert"
        class="mx-1 mb-1"
      >
        <v-alert
          :text="$bct(alertMessage)"
          color="primary"
          type="info"
          variant="tonal"
        />
      </v-row>
    </v-card-text>
    <v-divider />

    <v-card-actions>
      <v-spacer />
      <StandardButton
        v-if="member.id"
        @click="update"
        :disabled="changed == false"
        :loading="processing"
        data-cy="update_employee"
        text="Update employee"
      />
      <StandardButton
        v-else
        @click="create"
        :disabled="changed == false"
        :loading="processing"
        data-cy="create_employee"
        text="Create employee"
      />
    </v-card-actions>
  </v-card>
</template>

<script setup>
import { useStore } from 'vuex';

// import ActionMenu from '@/shared/components/ActionMenu.vue';
import LabeledAutocomplete from '@/shared/components/form/LabeledAutocomplete.vue';
import LabeledDatePicker from '@/shared/components/form/LabeledDatePicker.vue';
import LabeledSimpleSelect from '@/shared/components/form/LabeledSimpleSelect.vue';
import LabeledSwitch from '@/shared/components/form/LabeledSwitch.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import useApi from '@/shared/composables/useApi';
// import useConfirmDialog from '@/shared/composables/useConfirmDialog';
import useEmailValidation from '@/shared/composables/useEmailValidation';
import useEventBus from '@/shared/composables/useEventBus';
import useRole from '@/shared/composables/useRole';
import useRolePermissions from '@/shared/composables/useRolePermissions';

const { api, scopedApi } = useApi();
const { validateEmail } = useEmailValidation();
const eventBus = useEventBus();
const store = useStore();
const { businessEdit } = useRolePermissions();
const { isSpecialist } = useRole();

const $bct = inject('translator');

const props = defineProps({
  border: {
    type: Boolean,
    default: true,
  },
  fourColumnContactLayout: {
    type: Boolean,
    default: false,
  },
  fourColumnEmploymentLayout: {
    type: Boolean,
    default: false,
  },
  hideEmploymentAlert: {
    type: Boolean,
    default: false,
  },
  hideLocationsField: {
    type: Boolean,
    default: false,
  },
  hideMemberHeader: {
    type: Boolean,
    default: false,
  },
  member: {
    type: Object,
    default: null,
  },
  providers: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['change']);

const changed = ref(false);
// const confirm = useConfirmDialog();
// const memberActionItems = ref([{ title: 'Dismiss', avatar: 'delete', event: 'delete' }]);
const processing = ref(false);

const alertMessage = computed(() => {
  if (isSpecialist) {
    return "Changes are being made to employment management. To modify this member's location access, visit the Business Staff page for this member.";
  }
  return "To modify this user's access, add or remove Locations from the Locations field. To revoke all location access, enter an Employment End Date.";
});

const readOnly = computed(() => {
  if (businessEdit) return false; // Specialists can edit staff
  return !store.getters.showBusinessAdminPages();
});

const contactFieldCols = computed(() => (props.fourColumnContactLayout ? 3 : 4));
const employmentFieldCols = computed(() => (props.fourColumnEmploymentLayout ? 3 : 4));

function providerIdsForNestedEmployment() {
  const list = props.providers ?? [];
  const fallback = list.length > 0 ? [list[0].id] : [];
  const ids = props.member.provider_ids;
  if (Array.isArray(ids) && ids.length > 0) return ids;
  return fallback;
}

function buildEmploymentData() {
  const businessId = props.providers?.[0]?.business_id;
  const employmentId =
    props.member.employment_record_id ||
    props.member.employments?.find((employment) => employment.business_id === businessId)?.id ||
    props.member.employments?.[0]?.id;

  props.member.employments_attributes = [
    {
      business_id: businessId,
      business_messages_access: props.member.business_messages_access,
      business_role: props.member.business_role,
      end_date: props.member.end_date,
      id: employmentId,
      provider_ids: providerIdsForNestedEmployment(),
      start_date: props.member.start_date,
      title: props.member.title,
    },
  ];
}

async function saveMember({ createMember, updateMember }) {
  if (props.member.email && !validateEmail(props.member.email)) {
    return;
  }

  processing.value = true;
  buildEmploymentData();

  try {
    const response = props.member.id ? await updateMember() : await createMember();

    if (response?.status !== 200 && response?.status !== 201) {
      processing.value = false;
      return;
    }

    processChange();
  } catch (error) {
    processing.value = false;
    eventBus.error(error);
  }
}

function processChange() {
  eventBus.chime('Saved');
  processing.value = false;
  changed.value = false;
  emit('change');
}

// Temporarily removed as part of CARE-3753, will restore after employment management iteration
// async function dismissStaffMember() {
//   const confirmed = await confirm(
//     'Are you sure you want to dismiss this staff member? This will terminate their employment record.',
//   );
//   if (!confirmed) return;

//   const dismissParams = {
//     employments_attributes: [
//       {
//         id: props.member.employments?.[0]?.id,
//         provider_ids: providerIdsForNestedEmployment(),
//         end_date: new Date(),
//       },
//     ],
//   };
// TODO: This component is being used by specialists (via the shared ProviderStaffIndex.vue), this needs
// to be updated to use scopedApi
//   const response = await Api.manager.member.update(props.member.id, dismissParams);
//   if (!response || response.status !== 200) return;

//   processChange();
// }

async function create() {
  await saveMember({
    createMember: () =>
      isSpecialist
        ? scopedApi.member.create(props.member)
        : api.manager.member.create(props.member),
    updateMember: () => Promise.resolve(null),
  });
}

async function update() {
  await saveMember({
    createMember: () => Promise.resolve(null),
    updateMember: () =>
      isSpecialist
        ? scopedApi.member.update(props.member)
        : api.manager.member.update(props.member.id, props.member),
  });
}
</script>
